import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import tradingCostingService from "../services/tradingCostingService";
import AsyncSearchSelect from "../../ppe/pages/procurement/purchaseOrder/components/AsyncSearchSelect";
import lookupService from "../../ppe/pages/procurement/purchaseOrder/services/lookupService";
import POTermsConditionsDrawer from "../../ppe/pages/procurement/purchaseOrder/components/POTermsConditionsDrawer";

const styles = {
  page: {
    padding: "24px",
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "22px",
    //background: "linear-gradient(120deg, #0F172A 0%, #164E63 55%, #0891B2 100%)",
    background: "linear-gradient(120deg, #064E3B 0%, #047857 55%, #34D399 100%)",
    color: "#FFFFFF",
    padding: "22px",
    borderRadius: "14px",
  },
  title: { margin: 0, fontSize: "25px", fontWeight: 700 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: "13px" },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: "12px",
    padding: "18px",
    marginBottom: "18px",
  },
  sectionTitle: { margin: "0 0 16px", fontSize: "16px", fontWeight: 700 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "16px",
  },
  label: {
    display: "block",
    marginBottom: "7px",
    color: "#747b8d",
    fontSize: "12px",
    fontWeight: 600,
  },
  value: { fontSize: "14px", fontWeight: 600, overflowWrap: "anywhere" },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dfe2eb",
    borderRadius: "7px",
    padding: "9px 10px",
    fontSize: "13px",
    background: "#fff",
    color: "#252b3b",
  },
  readonly: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #edf0f5",
    borderRadius: "7px",
    padding: "9px 10px",
    fontSize: "13px",
    background: "#f7f8fc",
    color: "#747b8d",
  },
  button: {
    border: 0,
    borderRadius: "8px",
    padding: "11px 17px",
    background: "#6254d9",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dcd8ff",
    borderRadius: "8px",
    padding: "10px 15px",
    background: "#fff",
    color: "#5548cb",
    fontWeight: 600,
    cursor: "pointer",
  },
  error: {
    padding: "12px 14px",
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  success: {
    padding: "12px 14px",
    background: "#edf9f0",
    color: "#18743a",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
  },
  badge: {
    display: "inline-block",
    borderRadius: "20px",
    padding: "5px 10px",
    fontSize: "11px",
    fontWeight: 700,
    background: "#efedff",
    color: "#5548cb",
  },
  tableWrap: { width: "100%", overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1650px",
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    background: "#f7f8fc",
    color: "#70778a",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: ".3px",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 10px",
    fontSize: "12px",
    borderBottom: "1px solid #f0f1f6",
    verticalAlign: "middle",
  },
  amount: { fontSize: "14px", fontWeight: 700 },
  muted: { color: "#858b9b", fontSize: "13px" },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    padding: "9px 0",
    fontSize: "13px",
    borderBottom: "1px solid #f0f1f6",
  },
};

const unwrap = (response) =>
  response?.data?.data ?? response?.data ?? {};

const numberValue = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

const amount = (value, currency = "INR") => {
  const number = numberValue(value);

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    return number.toFixed(2);
  }
};

const decimal = (value, digits = 2) =>
  numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const asInput = (value) =>
  value === null || value === undefined ? "0" : String(value);

const editableStatuses = new Set(["DRAFT"]);

function Field({ label, children }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

export default function TradingCostingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [costing, setCosting] = useState(null);
  const [items, setItems] = useState([]);
  const [charges, setCharges] = useState([]);
  const [terms, setTerms] = useState([]);
  const [showTermsDrawer, setShowTermsDrawer] = useState(false);

  const [header, setHeader] = useState({
    margin_method: "MARKUP",
    margin_percent: "0",
    remarks: "",
  });


  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const status = String(costing?.status_code || "DRAFT").toUpperCase();
  const canEdit =
    editableStatuses.has(status) && costing?.is_current_version !== false;

  const loadCosting = useCallback(async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await tradingCostingService.getCosting(id);
      const data = unwrap(response);
      const loadedItems = Array.isArray(data.items) ? data.items : [];
      const loadedCharges = Array.isArray(data.charges) ? data.charges : [];

      setCosting(data);
      setTerms(Array.isArray(data.terms) ? data.terms : []);
      setCharges(loadedCharges.map((charge) => ({
        ...charge,
        charge_type: charge.charge_type || "",
        payer_type: charge.payer_type || "COMPANY",
        customer_id: charge.customer_id == null ? "" : String(charge.customer_id),
        charge_amount: asInput(charge.charge_amount),
        gst_rate: asInput(charge.gst_rate),
        remarks: charge.remarks || "",
      })));
      setItems(
        loadedItems.map((item) => ({
          ...item,
          quantity: asInput(item.quantity),
          unit_purchase_cost: asInput(item.unit_purchase_cost),
          purchase_discount_percent: asInput(
            item.purchase_discount_percent
          ),
          additional_cost: asInput(item.additional_cost),
          margin_method: item.margin_method || data.margin_method || "MARKUP",
          margin_percent: asInput(
            item.margin_percent ?? data.margin_percent ?? 0
          ),
          selling_discount_percent: asInput(
            item.selling_discount_percent
          ),
        }))
      );

      setHeader({
        margin_method: data.margin_method || "MARKUP",
        margin_percent: asInput(data.margin_percent),
        remarks: data.remarks || "",
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load Trading Costing."
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCosting();
  }, [loadCosting]);

  const updateItem = (index, field, value) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
    setMessage("");
  };

  const updateHeader = (field, value) => {
    setHeader((current) => ({ ...current, [field]: value }));
    setMessage("");
  };

  const computedItems = items.map((item) => {
    const quantity = Math.max(0, numberValue(item.quantity));
    const purchasePrice = Math.max(
      0,
      numberValue(item.unit_purchase_cost)
    );
    const purchaseDiscountPercent = Math.min(
      100,
      Math.max(0, numberValue(item.purchase_discount_percent))
    );
    const additionalCost = Math.max(0, numberValue(item.additional_cost));
    const sellingDiscountPercent = Math.min(
      100,
      Math.max(0, numberValue(item.selling_discount_percent))
    );

    const purchaseGross = quantity * purchasePrice;
    const purchaseDiscount =
      (purchaseGross * purchaseDiscountPercent) / 100;
    const purchaseTaxable = purchaseGross - purchaseDiscount;
    const totalCost = purchaseTaxable + additionalCost;
    const landedUnitCost = quantity > 0 ? totalCost / quantity : 0;

    const marginMethod = item.margin_method || header.margin_method;
    const marginPercent = Math.max(
      0,
      numberValue(item.margin_percent ?? header.margin_percent)
    );

    let unitSellingPrice = landedUnitCost;

    if (marginMethod === "MARGIN") {
      unitSellingPrice =
        marginPercent < 100
          ? landedUnitCost / (1 - marginPercent / 100)
          : 0;
    } else {
      unitSellingPrice =
        landedUnitCost * (1 + marginPercent / 100);
    }

    const lineSellingAmount = unitSellingPrice * quantity;
    const sellingDiscount =
      (lineSellingAmount * sellingDiscountPercent) / 100;
    const sellingTaxable = lineSellingAmount - sellingDiscount;

    // GST amounts and percentages come from saved backend values.
    // The frontend does not calculate or hardcode tax rates.
    const sellingCgst = numberValue(item.selling_cgst_amount);
    const sellingSgst = numberValue(item.selling_sgst_amount);
    const sellingIgst = numberValue(item.selling_igst_amount);
    const sellingOtherTax = numberValue(item.selling_other_tax_amount);
    const sellingTax =
      sellingCgst + sellingSgst + sellingIgst + sellingOtherTax;

    return {
      ...item,
      preview_purchase_gross: purchaseGross,
      preview_purchase_discount: purchaseDiscount,
      preview_purchase_taxable: purchaseTaxable,
      preview_total_cost: totalCost,
      preview_landed_unit_cost: landedUnitCost,
      preview_unit_selling_price: unitSellingPrice,
      preview_line_selling_amount: lineSellingAmount,
      preview_selling_discount: sellingDiscount,
      preview_selling_taxable: sellingTaxable,
      preview_selling_cgst: sellingCgst,
      preview_selling_sgst: sellingSgst,
      preview_selling_igst: sellingIgst,
      preview_selling_tax: sellingTax,
      preview_selling_amount: sellingTaxable + sellingTax,
    };
  });

  const totals = computedItems.reduce(
    (sum, item) => {
      sum.purchaseGross += item.preview_purchase_gross;
      sum.purchaseDiscount += item.preview_purchase_discount;
      sum.purchaseTaxable += item.preview_purchase_taxable;
      sum.additionalCost += numberValue(item.additional_cost);
      sum.totalCost += item.preview_total_cost;
      sum.sellingGross += item.preview_line_selling_amount;
      sum.sellingDiscount += item.preview_selling_discount;
      sum.sellingTaxable += item.preview_selling_taxable;
      sum.sellingCgst += item.preview_selling_cgst;
      sum.sellingSgst += item.preview_selling_sgst;
      sum.sellingIgst += item.preview_selling_igst;
      sum.sellingTax += item.preview_selling_tax;
      sum.sellingAmount += item.preview_selling_amount;
      return sum;
    },
    {
      purchaseGross: 0,
      purchaseDiscount: 0,
      purchaseTaxable: 0,
      additionalCost: 0,
      totalCost: 0,
      sellingGross: 0,
      sellingDiscount: 0,
      sellingTaxable: 0,
      sellingCgst: 0,
      sellingSgst: 0,
      sellingIgst: 0,
      sellingTax: 0,
      sellingAmount: 0,
    }
  );
  // ---------------------------------------------------------------------------
  // ADDITIONAL CHARGES
  // ---------------------------------------------------------------------------

  const updateCharge = (index, field, value) => {
    setCharges((current) =>
      current.map((charge, chargeIndex) =>
        chargeIndex === index
          ? { ...charge, [field]: value }
          : charge
      )
    );
    setMessage("");
  };

  const addCharge = () => {
    setCharges((current) => [
      ...current,
      {
        charge_type: "",
        payer_type: "COMPANY",
        customer_id: "",
        charge_amount: "0",
        gst_rate: "0",
        remarks: "",
      },
    ]);
    setMessage("");
  };

  const removeCharge = (index) => {
    setCharges((current) =>
      current.filter((_, chargeIndex) => chargeIndex !== index)
    );
    setMessage("");
  };

  const computedCharges = charges.map((charge) => {
    const chargeAmount = Math.max(
      0,
      numberValue(charge.charge_amount)
    );
    const gstRate = Math.max(
      0,
      numberValue(charge.gst_rate)
    );
    const gstAmount = (chargeAmount * gstRate) / 100;

    return {
      ...charge,
      preview_gst_amount: gstAmount,
      preview_total: chargeAmount + gstAmount,
    };
  });

    const chargeTotals = computedCharges
    .filter((charge) => charge.payer_type === "CUSTOMER")
    .reduce(
      (sum, charge) => ({
        amount: sum.amount + numberValue(charge.charge_amount),
        gst: sum.gst + numberValue(charge.preview_gst_amount),
        total: sum.total + numberValue(charge.preview_total),
      }),
      {
        amount: 0,
        gst: 0,
        total: 0,
      }
    );

  const finalSellingAmount = totals.sellingAmount + chargeTotals.total;
  
    const approveCosting = async () => {
    if (status !== "DRAFT" || costing?.is_current_version === false) {
      setError("Only the current DRAFT costing can be approved.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to approve this costing? Approved costing cannot be edited."
    );

    if (!confirmed) return;

    setError("");
    setMessage("");
    setSaving(true);

    try {
      await tradingCostingService.approveCosting(id);

      await loadCosting();

      setMessage("Trading Costing approved successfully.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to approve Trading Costing."
      );
    } finally {
      setSaving(false);
    }
  };
  const saveCosting = async () => {
    setError("");
    setMessage("");

    if (!canEdit) {
      setError("Only the current DRAFT costing can be edited.");
      return;
    }

    if (items.length === 0) {
      setError("This costing has no item lines to save.");
      return;
    }

    const invalidItem = items.find(
      (item) =>
        !Number.isFinite(Number(item.quantity)) ||
        Number(item.quantity) <= 0 ||
        !Number.isFinite(Number(item.unit_purchase_cost)) ||
        Number(item.unit_purchase_cost) < 0 ||
        !Number.isFinite(Number(item.purchase_discount_percent)) ||
        Number(item.purchase_discount_percent) < 0 ||
        Number(item.purchase_discount_percent) > 100 ||
        !Number.isFinite(Number(item.additional_cost)) ||
        Number(item.additional_cost) < 0 ||
        !Number.isFinite(Number(item.selling_discount_percent)) ||
        Number(item.selling_discount_percent) < 0 ||
        Number(item.selling_discount_percent) > 100
    );

    if (invalidItem) {
      setError(
        `Please check quantity, purchase cost, discounts and additional cost for ${
          invalidItem.item_description || `item ${invalidItem.raw_material_id}`
        }.`
      );
      return;
    }

    if (
      !["MARKUP", "MARGIN"].includes(header.margin_method) ||
      !Number.isFinite(Number(header.margin_percent)) ||
      Number(header.margin_percent) < 0 ||
      Number(header.margin_percent) >= 100
    ) {
      setError("Margin method or margin percentage is invalid.");
      return;
    }

    console.log(
  "Selected customer values:",
  charges.map((charge) => ({
    payer_type: charge.payer_type,
    customer_id: charge.customer_id,
    customer_id_type: typeof charge.customer_id,
  }))
);

    const invalidCharge = charges.find((charge) => {
  const customerId =
    charge.customer_id === null ||
    charge.customer_id === undefined
      ? ""
      : String(charge.customer_id).trim();

  const invalidCustomerId =
    charge.payer_type === "CUSTOMER" &&
    (
      customerId === "" ||
      !Number.isInteger(Number(customerId)) ||
      Number(customerId) <= 0
    );

  const invalidOptionalCustomerId =
    customerId !== "" &&
    (
      !Number.isInteger(Number(customerId)) ||
      Number(customerId) <= 0
    );

  return (
    !String(charge.charge_type || "").trim() ||
    !["COMPANY", "CUSTOMER", "VENDOR"].includes(charge.payer_type) ||
    !Number.isFinite(Number(charge.charge_amount)) ||
    Number(charge.charge_amount) < 0 ||
    !Number.isFinite(Number(charge.gst_rate)) ||
    Number(charge.gst_rate) < 0 ||
    Number(charge.gst_rate) > 100 ||
    invalidCustomerId ||
    invalidOptionalCustomerId
  );
});

if (invalidCharge) {
  const customerId =
    invalidCharge.customer_id == null
      ? ""
      : String(invalidCharge.customer_id).trim();

  let validationMessage = "Please check the charge details.";

  if (!String(invalidCharge.charge_type || "").trim()) {
    validationMessage = "Please select a Charge Type.";
  } else if (
    !["COMPANY", "CUSTOMER", "VENDOR"].includes(invalidCharge.payer_type)
  ) {
    validationMessage = "Please select a valid Payer Type.";
  } else if (
    invalidCharge.payer_type === "CUSTOMER" &&
    (
      customerId === "" ||
      !Number.isInteger(Number(customerId)) ||
      Number(customerId) <= 0
    )
  ) {
    validationMessage = "Please select a valid Customer for this charge.";
  } else if (
    customerId !== "" &&
    (!Number.isInteger(Number(customerId)) || Number(customerId) <= 0)
  ) {
    validationMessage = "Customer ID must be a valid positive integer.";
  } else if (
    !Number.isFinite(Number(invalidCharge.charge_amount)) ||
    Number(invalidCharge.charge_amount) < 0
  ) {
    validationMessage = "Charge Amount must be zero or greater.";
  } else if (
    !Number.isFinite(Number(invalidCharge.gst_rate)) ||
    Number(invalidCharge.gst_rate) < 0 ||
    Number(invalidCharge.gst_rate) > 100
  ) {
    validationMessage = "GST Rate must be between 0 and 100.";
  }

  setError(validationMessage);
  return;
}

    const payload = {
      margin_method: header.margin_method,
      margin_percent: Number(header.margin_percent),
      remarks: header.remarks,
      terms: terms,
      charges: charges.map((charge) => ({
        charge_type: charge.charge_type.trim(),
        payer_type: charge.payer_type,
        customer_id: charge.customer_id === "" ? null : Number(charge.customer_id),
        charge_amount: Number(charge.charge_amount),
        gst_rate: Number(charge.gst_rate),
        remarks: charge.remarks.trim() || null,
      })),
      items: items.map((item) => ({
        enquiry_item_id: item.enquiry_item_id,
        quantity: Number(item.quantity),
        unit_purchase_cost: Number(item.unit_purchase_cost),
        purchase_discount_percent: Number(
          item.purchase_discount_percent
        ),
        additional_cost: Number(item.additional_cost),
        margin_method: item.margin_method || header.margin_method,
        margin_percent: Number(
          item.margin_percent ?? header.margin_percent
        ),
        selling_discount_percent: Number(
          item.selling_discount_percent
        ),
      })),
    };

    setSaving(true);

    try {
      const response = await tradingCostingService.updateCosting(
        id,
        payload
      );
      const data = unwrap(response);

      setCosting(data);
      setTerms(Array.isArray(data.terms) ? data.terms : []);
      setCharges(
  (Array.isArray(data.charges) ? data.charges : []).map((charge) => ({
    ...charge,
    charge_type: charge.charge_type || "",
    payer_type: charge.payer_type || "COMPANY",
    customer_id:
      charge.customer_id == null ? "" : String(charge.customer_id),
    customer: charge.customer || null,
    charge_amount: asInput(charge.charge_amount),
    gst_rate: asInput(charge.gst_rate),
    remarks: charge.remarks || "",
  }))
);
      setItems(
        (Array.isArray(data.items) ? data.items : []).map((item) => ({
          ...item,
          quantity: asInput(item.quantity),
          unit_purchase_cost: asInput(item.unit_purchase_cost),
          purchase_discount_percent: asInput(
            item.purchase_discount_percent
          ),
          additional_cost: asInput(item.additional_cost),
          margin_method: item.margin_method || data.margin_method || "MARKUP",
          margin_percent: asInput(
            item.margin_percent ?? data.margin_percent ?? 0
          ),
          selling_discount_percent: asInput(
            item.selling_discount_percent
          ),
        }))
      );
      setHeader({
        margin_method: data.margin_method || header.margin_method,
        margin_percent: asInput(data.margin_percent),
        remarks: data.remarks || "",
      });
      setMessage("Trading Costing saved successfully.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to save Trading Costing."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Loading Trading Costing...</div>
      </div>
    );
  }

  if (!costing && error) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <h1 style={styles.title}>Trading Costing</h1>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/costings")}
          >
            Back to Costings
          </button>
        </div>
        <div style={styles.error}>{error}</div>
        <button
          type="button"
          style={styles.button}
          onClick={loadCosting}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!costing) return null;
    return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            {costing.costing_no || `Costing ${costing.id}`}
          </h1>
          <p style={styles.subtitle}>
            Trading Costing · Enquiry #{costing.enquiry_id} · Version{" "}
            {costing.version_no ?? 1}
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <span style={styles.badge}>{status}</span>
                  {/* APPROVE BUTTON */}
          {status === "DRAFT" &&
            costing?.is_current_version !== false && (
              <button
                type="button"
                style={{
                  ...styles.button,
                  background: "#18743a",
                  opacity: saving ? 0.6 : 1,
                }}
                onClick={approveCosting}
                disabled={saving}
              >
                {saving ? "Processing..." : "Approve"}
              </button>
            )}

                    <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/costings")}
          >
            Back to Costings
          </button>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => setShowTermsDrawer(true)}
            disabled={!canEdit}
          >
            Terms & Conditions
          </button>

          {canEdit && (
            <button
              type="button"
              style={{
                ...styles.button,
                opacity: saving ? 0.6 : 1,
              }}
              disabled={saving}
              onClick={saveCosting}
            >
              {saving ? "Saving..." : "Save Costing"}
            </button>
          )}
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {message && <div style={styles.success}>{message}</div>}

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Costing Details</h2>

        <div style={styles.grid}>
          <Field label="Costing Number">
            <div style={styles.value}>
              {costing.costing_no || costing.id || "—"}
            </div>
          </Field>

          <Field label="Enquiry Number">
            <div style={styles.value}>
              {costing.enquiry_no || costing.enquiry_id || "—"}
            </div>
          </Field>

          <Field label="Version">
            <div style={styles.value}>
              {costing.version_no ?? 1}
            </div>
          </Field>

          <Field label="Status">
            <div>
              <span style={styles.badge}>{status}</span>
            </div>
          </Field>

          <Field label="Margin Method">
            <select
              style={styles.input}
              value={header.margin_method}
              disabled={!canEdit}
              onChange={(event) =>
                updateHeader("margin_method", event.target.value)
              }
            >
              <option value="MARKUP">Markup</option>
              <option value="MARGIN">Margin</option>
            </select>
          </Field>

          <Field label="Default Margin (%)">
            <input
              type="number"
              min="0"
              max="99.9999"
              step="0.01"
              style={styles.input}
              value={header.margin_percent}
              disabled={!canEdit}
              onChange={(event) =>
                updateHeader("margin_percent", event.target.value)
              }
            />
          </Field>

          <Field label="Remarks">
            <input
              type="text"
              style={styles.input}
              value={header.remarks}
              disabled={!canEdit}
              onChange={(event) =>
                updateHeader("remarks", event.target.value)
              }
              placeholder="Enter remarks"
            />
          </Field>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Item Costing</h2>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item</th>
                <th style={styles.th}>HSN Code</th>
                <th style={styles.th}>CGST %</th>
                <th style={styles.th}>SGST %</th>
                <th style={styles.th}>IGST %</th>
                <th style={styles.th}>Qty</th>
                <th style={styles.th}>Purchase Rate</th>
                <th style={styles.th}>Purchase Discount %</th>
                <th style={styles.th}>Additional Cost</th>
                <th style={styles.th}>Total Cost</th>
                <th style={styles.th}>Landed Unit Cost</th>
                <th style={styles.th}>Margin Method</th>
                <th style={styles.th}>Margin %</th>
                <th style={styles.th}>Selling Discount %</th>
                <th style={styles.th}>Unit Selling Price</th>
                <th style={styles.th}>Selling Taxable</th>
              </tr>
            </thead>

            <tbody>
              {computedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={16}
                    style={{ ...styles.td, textAlign: "center" }}
                  >
                    No costing item lines found.
                  </td>
                </tr>
              ) : (
                computedItems.map((item, index) => (
                  <tr key={item.id || item.enquiry_item_id || index}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 700 }}>
                        {item.material_name ||
                          item.item_description ||
                          `Raw Material #${item.raw_material_id}`}
                      </div>
                      <div style={styles.muted}>
                        {item.material_code
                          ? `${item.material_code} · `
                          : ""}
                        Material ID: {item.raw_material_id ?? "—"}
                      </div>
                    </td>

                    <td style={styles.td}>{item.hsn_code || "—"}</td>
                    <td style={styles.td}>
                      {decimal(item.selling_cgst_percent)}%
                    </td>
                    <td style={styles.td}>
                      {decimal(item.selling_sgst_percent)}%
                    </td>
                    <td style={styles.td}>
                      {decimal(item.selling_igst_percent)}%
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0.000001"
                        step="0.000001"
                        style={{ ...styles.input, minWidth: "90px" }}
                        value={item.quantity}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(index, "quantity", event.target.value)
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        style={{ ...styles.input, minWidth: "110px" }}
                        value={item.unit_purchase_cost}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "unit_purchase_cost",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "90px" }}
                        value={item.purchase_discount_percent}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "purchase_discount_percent",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "110px" }}
                        value={item.additional_cost}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "additional_cost",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      {amount(
                        item.preview_total_cost,
                        costing.currency_code || costing.currency
                      )}
                    </td>

                    <td style={styles.td}>
                      {amount(
                        item.preview_landed_unit_cost,
                        costing.currency_code || costing.currency
                      )}
                    </td>

                    <td style={styles.td}>
                      <select
                        style={{ ...styles.input, minWidth: "135px" }}
                        value={item.margin_method || header.margin_method}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "margin_method",
                            event.target.value
                          )
                        }
                      >
                        <option value="MARKUP">Markup</option>
                        <option value="MARGIN">Margin</option>
                      </select>
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        max="99.9999"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "80px" }}
                        value={item.margin_percent}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "margin_percent",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "90px" }}
                        value={item.selling_discount_percent}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateItem(
                            index,
                            "selling_discount_percent",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      {amount(
                        item.preview_unit_selling_price,
                        costing.currency_code || costing.currency
                      )}
                    </td>

                    <td style={styles.td}>
                      {amount(
                        item.preview_selling_taxable,
                        costing.currency_code || costing.currency
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p style={{ ...styles.muted, marginBottom: 0 }}>
          Item amounts are previews. The backend remains authoritative for
          persisted costing totals and tax calculations.
        </p>
      </div>

      {/* ADDITIONAL CHARGES */}
      <div style={styles.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div>
            <h2 style={{ ...styles.sectionTitle, marginBottom: "5px" }}>
              Additional Charges
            </h2>
            <p style={styles.muted}>
              Record charges separately from item-level additional costs.
            </p>
          </div>

          {canEdit && (
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={addCharge}
            >
              + Add Charge
            </button>
          )}
        </div>

        {charges.length === 0 ? (
          <p style={styles.muted}>
            No additional charges have been added.
          </p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={{ ...styles.table, minWidth: "1150px" }}>
              <thead>
                <tr>
                  <th style={styles.th}>Charge Type</th>
                  <th style={styles.th}>Payer</th>
                  <th style={styles.th}>Customer ID</th>
                  <th style={styles.th}>Charge Amount</th>
                  <th style={styles.th}>GST Rate (%)</th>
                  <th style={styles.th}>GST Amount</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Remarks</th>
                  {canEdit && <th style={styles.th}>Action</th>}
                </tr>
              </thead>

              <tbody>
                {computedCharges.map((charge, index) => (
                  <tr key={charge.id || `new-charge-${index}`}>
                    <td style={styles.td}>
                    <select
                      style={{ ...styles.input, minWidth: "140px" }}
                      value={charge.charge_type || ""}
                      disabled={!canEdit}
                      onChange={(event) =>
                        updateCharge(index, "charge_type", event.target.value)
                      }
                    >
                      <option value="">Select Charge Type</option>
                      <option value="FREIGHT">Freight</option>
                      <option value="LOADING">Loading</option>
                      <option value="UNLOADING">Unloading</option>
                      <option value="INSURANCE">Insurance</option>
                      <option value="PACKING">Packing</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </td>

                    <td style={styles.td}>
                      <select
                        style={{ ...styles.input, minWidth: "125px" }}
                        value={charge.payer_type}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateCharge(
                            index,
                            "payer_type",
                            event.target.value
                          )
                        }
                      >
                        <option value="COMPANY">Company</option>
                        <option value="CUSTOMER">Customer</option>
                        <option value="VENDOR">Vendor</option>
                      </select>
                    </td>

                    <td style={styles.td}>
                      <div style={{ minWidth: "200px" }}>
                       <AsyncSearchSelect
                          value={charge.customer || null}
                          onChange={(customer) => {
                            updateCharge(index, "customer", customer || null);
                            updateCharge(
                              index,
                              "customer_id",
                              customer?.id == null ? "" : String(customer.id)
                            );
                          }}
                          loadOptions={(search) =>
                            lookupService.searchCustomers(search)
                          }
                          labelKey="name"
                          valueKey="id"
                          placeholder={
                            charge.payer_type === "CUSTOMER"
                              ? "Select Customer *"
                              : "Not applicable"
                          }
                          isDisabled={!canEdit || charge.payer_type !== "CUSTOMER"}
                        />
                        {charge.payer_type === "CUSTOMER" &&
                          !charge.customer_id && (
                            <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                              Customer is required
                            </div>
                          )}
                      </div>
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "115px" }}
                        value={charge.charge_amount}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateCharge(
                            index,
                            "charge_amount",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        style={{ ...styles.input, minWidth: "90px" }}
                        value={charge.gst_rate}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateCharge(
                            index,
                            "gst_rate",
                            event.target.value
                          )
                        }
                      />
                    </td>

                    <td style={styles.td}>
                      {amount(
                        charge.preview_gst_amount,
                        costing.currency_code || costing.currency
                      )}
                    </td>

                    <td style={styles.td}>
                      <strong>
                        {amount(
                          charge.preview_total,
                          costing.currency_code || costing.currency
                        )}
                      </strong>
                    </td>

                    <td style={styles.td}>
                      <input
                        type="text"
                        style={{ ...styles.input, minWidth: "160px" }}
                        value={charge.remarks}
                        disabled={!canEdit}
                        onChange={(event) =>
                          updateCharge(
                            index,
                            "remarks",
                            event.target.value
                          )
                        }
                        placeholder="Optional remarks"
                      />
                    </td>

                    {canEdit && (
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={{
                            ...styles.secondaryButton,
                            color: "#b42318",
                            borderColor: "#f2c7c7",
                          }}
                          onClick={() => removeCharge(index)}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div
          style={{
            marginTop: "18px",
            marginLeft: "auto",
            maxWidth: "420px",
          }}
        >
          <div style={styles.summaryRow}>
            <span>Charges Subtotal</span>
            <strong>
              {amount(chargeTotals.amount, costing.currency_code || costing.currency)}
            </strong>
          </div>

          <div style={styles.summaryRow}>
            <span>Charges GST Preview</span>
            <strong>
              {amount(chargeTotals.gst, costing.currency_code || costing.currency)}
            </strong>
          </div>

          <div
            style={{
              ...styles.summaryRow,
              borderBottom: "none",
              fontSize: "15px",
            }}
          >
            <span>Total Charges Preview</span>
            <strong>
              {amount(chargeTotals.total, costing.currency_code || costing.currency)}
            </strong>
          </div>
        </div>

        <p style={styles.muted}>
        Customer charges and their GST are included in the Final Selling
        Amount Including GST. Company and Vendor charges are excluded.
        All charges remain separate from item-level additional costs.
      </p>
      </div>

      {/* COSTING SUMMARY */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Costing Summary</h2>

        <div style={styles.grid}>
          <div>
            <div style={styles.label}>Purchase Gross</div>
            <div style={styles.amount}>
              {amount(totals.purchaseGross, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Purchase Discount</div>
            <div style={styles.amount}>
              {amount(totals.purchaseDiscount, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Purchase Taxable</div>
            <div style={styles.amount}>
              {amount(totals.purchaseTaxable, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Item Additional Cost</div>
            <div style={styles.amount}>
              {amount(totals.additionalCost, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Total Item Cost</div>
            <div style={styles.amount}>
              {amount(totals.totalCost, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Selling Gross</div>
            <div style={styles.amount}>
              {amount(totals.sellingGross, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Selling Discount</div>
            <div style={styles.amount}>
              {amount(totals.sellingDiscount, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Selling Taxable</div>
            <div style={styles.amount}>
              {amount(totals.sellingTaxable, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Selling GST</div>
            <div style={styles.amount}>
              {amount(totals.sellingTax, costing.currency_code || costing.currency)}
            </div>
          </div>

          <div>
            <div style={styles.label}>Selling Amount Including GST</div>
            <div style={styles.amount}>
              {amount(totals.sellingAmount, costing.currency_code || costing.currency)}
            </div>
          </div>
          <div>
          <div style={styles.label}>Final Selling Amount Including GST</div>
          <div style={{ ...styles.amount, color: "#18743a" }}>
            {amount(finalSellingAmount, costing.currency_code || costing.currency)}
          </div>
        </div>
        </div>
      </div>

      {canEdit && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "20px",
          }}
        >
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={loadCosting}
            disabled={saving}
          >
            Reset Changes
          </button>

          <button
            type="button"
            style={styles.button}
            onClick={saveCosting}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Costing"}
          </button>
        </div>
            )}

      {showTermsDrawer && (
        <POTermsConditionsDrawer
          terms={terms}
          setTerms={setTerms}
          onClose={() => setShowTermsDrawer(false)}
        />
      )}
    </div>
  );
}